import React, { useState } from 'react';
import {
  withLoading,
  useTranslator,
  withMobileAppMode,
  useAppContext,
  useToast,
  callNativeMobileApp,
  callNativeAppRemoveLastRoute,
} from '@front-activerse/common-components';

import { Container, Row, Col, Input, Button } from 'reactstrap';
import Layout from '../Layout/Layout';

import UniversalPlaceholder from '../Layout/UniversalPlaceholder';
import TitleBar from '../Layout/TitleBar';
import { MacroDonutChart } from '../Layout';
import { useNavigate, useLocation } from 'react-router-dom';

interface IMacroSettingsContainer {
  dietSettingsData: any;
  mobileAppMode: boolean;
  saveMacro: (input: any) => Promise<any>;
}

const MacroSettingsContainer: React.FC<IMacroSettingsContainer> = ({
  mobileAppMode,
  dietSettingsData,
  saveMacro,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const { toastError, toastSuccess } = useToast();
  const { state } = useLocation();
  const [isSaving, setIsSaving] = useState(false);

  const baseCalories = dietSettingsData.dailyTracker?.data?.baseCalories || 0;
  const macronutrientRatio = dietSettingsData.me.diet.macronutrientRatio || {
    protein: 0,
    fat: 0,
    carbohydrate: 0,
  };
  const preferredMacronutrientRatio = dietSettingsData.me.diet.preferredMacronutrientRatio || {
    protein: 0,
    fat: 0,
    carbohydrate: 0,
  };

  let macronutrientRatioToUse = macronutrientRatio;
  if (
    preferredMacronutrientRatio.protein !== 0 &&
    preferredMacronutrientRatio.fat !== 0 &&
    preferredMacronutrientRatio.carbohydrate !== 0
  ) {
    macronutrientRatioToUse = preferredMacronutrientRatio;
  }

  const {
    images: { minusMacro, plusMacro, chartIcon },
  } = useAppContext();

  const [protein, setProtein] = useState(macronutrientRatioToUse.protein);
  const [fat, setFat] = useState(macronutrientRatioToUse.fat);
  const [carbohydrate, setCarbohydrate] = useState(macronutrientRatioToUse.carbohydrate);

  const proteinMin = 10;
  const proteinMax = 40;
  const fatMin = 10;
  const fatMax = 80;
  const carbohydrateMin = 5;
  const carbohydrateMax = 75;

  const proteinGrams = Math.round(((protein / 100) * Number(baseCalories)) / 4);
  const fatGrams = Math.round(((fat / 100) * Number(baseCalories)) / 9);
  const carbohydrateGrams = Math.round(((carbohydrate / 100) * Number(baseCalories)) / 4);

  const macroPresets = [
    {
      id: 'standard',
      name: 'tracker.macroDistribution.preset.title.balanced',
      protein: 20,
      fat: 30,
      carbohydrate: 50,
    },
    {
      id: 'highProtein',
      name: 'tracker.macroDistribution.preset.title.highProtein',
      protein: 30,
      fat: 30,
      carbohydrate: 40,
    },
    {
      id: 'lowCarb',
      name: 'tracker.macroDistribution.preset.title.lowCarb',
      protein: 25,
      fat: 55,
      carbohydrate: 20,
    },
    {
      id: 'keto',
      name: 'tracker.macroDistribution.preset.title.keto',
      protein: 20,
      fat: 75,
      carbohydrate: 5,
    },
  ];

  const applyPreset = (preset: (typeof macroPresets)[0]) => {
    setProtein(preset.protein);
    setFat(preset.fat);
    setCarbohydrate(preset.carbohydrate);
  };

  const handleMacroChange = (value: number, macro: 'protein' | 'fat' | 'carbohydrate') => {
    switch (macro) {
      case 'protein':
        setProtein(protein + value);
        if (protein + value < proteinMin) {
          setProtein(proteinMin);
        }
        if (protein + value > proteinMax) {
          setProtein(proteinMax);
        }
        break;
      case 'fat':
        setFat(fat + value);
        if (fat + value < fatMin) {
          setFat(fatMin);
        }
        if (fat + value > fatMax) {
          setFat(fatMax);
        }
        break;
      case 'carbohydrate':
        setCarbohydrate(carbohydrate + value);
        if (carbohydrate + value < carbohydrateMin) {
          setCarbohydrate(carbohydrateMin);
        }
        if (carbohydrate + value > carbohydrateMax) {
          setCarbohydrate(carbohydrateMax);
        }
        break;
    }
  };

  const total = protein + fat + carbohydrate;

  const handleSaveMacro = async () => {
    setIsSaving(true);
    const response = await saveMacro({
      macronutrientRatioSource: 'PREFERRED',
      preferredMacronutrientRatio: { protein, fat, carbohydrate },
    });
    if (response.__typename === 'DailyTrackerSettingsSuccess') {
      callNativeMobileApp({
        action: 'refresh_data',
        payload: {
          module: 'tracker',
        },
      });
      setIsSaving(false);
      toastSuccess(<p>{t('settings.notification.updateConfirmation')}</p>);

      if (state?.from?.pathname) {
        // when open in next page go back
        navigate(-1);
        callNativeAppRemoveLastRoute();
      } else {
        // when open in modal close modal
        callNativeMobileApp({
          action: 'close_modal',
        });
      }
    } else {
      toastError();
    }
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.macroSettings.navbarTitile')}</TitleBar>
      <section className="page-template-tracker-settings pt-md-4 full-height3">
        <Container>
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="tracker-container">
                  {/* Macro chart */}
                  <div className="macro-chart-container mb-l">
                    <MacroDonutChart
                      proteinPercentage={protein}
                      fatPercentage={fat}
                      carbsPercentage={carbohydrate}
                      missingPercentage={100 - total < 0 ? 0 : 100 - total}
                      chartId="macro-settings"
                      donutWidth={20}
                    />
                    <div className="text">
                      <p className="description medium mb-0 text-text-main-color">
                        {t('tracker.macroDistribution.totalValue.label')}
                      </p>

                      {total > 100 ? (
                        <p className="title-medium bold mb-0 text-text-error-color">{total}%</p>
                      ) : (
                        <p className="title-medium bold mb-0">{total}%</p>
                      )}
                    </div>
                  </div>

                  <p
                    className={
                      total > 100
                        ? 'small text-center text-text-error-color mb-l'
                        : 'small text-center text-text-variant-color mb-l'
                    }
                  >
                    {t('tracker.macroDistribution.totalValue.description')}
                  </p>
                  {/* Proteins */}
                  <div className="mb-xl">
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="semi-bold mb-0">{t('tracker.macros.proteins')}</p>
                      <div>
                        <span className="small text-text-main-color">{proteinGrams}g</span>
                        <span className="small percentage protein">{protein}%</span>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between gap-3">
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => handleMacroChange(-1, 'protein')}
                      >
                        <img src={minusMacro} alt="minus" />
                      </Button>
                      <Input
                        className="custom-range protein"
                        type="range"
                        name="protein"
                        id="protein"
                        min={10}
                        max={40}
                        unit={'%'}
                        step={1}
                        style={
                          {
                            '--value': `${((protein - 10) / (40 - 10)) * 100}%`,
                          } as React.CSSProperties
                        }
                        value={protein}
                        onChange={(e) => setProtein(Number(e.target.value))}
                      />
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => handleMacroChange(1, 'protein')}
                      >
                        <img src={plusMacro} alt="plus" />
                      </Button>
                    </div>
                  </div>
                  {/* Fats */}
                  <div className="mb-xl">
                    <div className="d-flex justify-content-between">
                      <p className="semi-bold mb-s">{t('tracker.macros.fats')}</p>
                      <div>
                        <span className="small text-text-main-color">{fatGrams}g</span>
                        <span className="small percentage fat">{fat}%</span>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between gap-3">
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => handleMacroChange(-1, 'fat')}
                      >
                        <img src={minusMacro} alt="minus" />
                      </Button>
                      <Input
                        className="custom-range fat"
                        type="range"
                        name="fat"
                        id="fat"
                        min={10}
                        max={80}
                        unit={'%'}
                        step={1}
                        style={
                          {
                            '--value': `${((fat - 10) / (80 - 10)) * 100}%`,
                          } as React.CSSProperties
                        }
                        value={fat}
                        onChange={(e) => setFat(Number(e.target.value))}
                      />
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => handleMacroChange(1, 'fat')}
                      >
                        <img src={plusMacro} alt="plus" />
                      </Button>
                    </div>
                  </div>
                  {/* Carbohydrates */}
                  <div className="mb-xl">
                    <div className="d-flex justify-content-between">
                      <p className="semi-bold mb-s">{t('tracker.macros.carbs')}</p>
                      <div>
                        <span className="small text-text-main-color">{carbohydrateGrams}g</span>
                        <span className="small percentage carbohydrate">{carbohydrate}%</span>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between gap-3">
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => handleMacroChange(-1, 'carbohydrate')}
                      >
                        <img src={minusMacro} alt="minus" />
                      </Button>
                      <Input
                        className="custom-range carbohydrate"
                        type="range"
                        name="carbohydrate"
                        id="carbohydrate"
                        min={5}
                        max={75}
                        unit={'%'}
                        step={1}
                        style={
                          {
                            '--value': `${((carbohydrate - 5) / (75 - 5)) * 100}%`,
                          } as React.CSSProperties
                        }
                        value={carbohydrate}
                        onChange={(e) => setCarbohydrate(Number(e.target.value))}
                      />
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => handleMacroChange(1, 'carbohydrate')}
                      >
                        <img src={plusMacro} alt="plus" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs="12" md={{ size: 10, offset: 1 }}>
              <div className="tracker-settings-box-container">
                <p className="medium mb-m">{t('tracker.presets.sectionTitle')}</p>
                <div className="presets-grid">
                  <Row>
                    {macroPresets.map((preset) => (
                      <Col xs="6" md="4" key={preset.id}>
                        <div className="preset-item" key={preset.id}>
                          <div className="d-flex align-items-center mb-m">
                            <img src={chartIcon} alt="chart" className="me-2" />
                            <p className="preset-name semi-bold mb-0">{t(preset.name)}</p>
                          </div>

                          <div className="d-flex gap-2 mb-l">
                            <div className="d-flex align-items-start">
                              <div className="bullet protein"></div>
                              <div>
                                <span className="small">
                                  {t('settings.dietType.macros.proteins')}
                                </span>
                                <br />
                                <span className="semi-bold">{preset.protein}%</span>
                              </div>
                            </div>

                            <div className="d-flex align-items-start">
                              <div className="bullet fat"></div>
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.fats')}
                                </span>
                                <br />
                                <span className="semi-bold">{preset.fat}%</span>
                              </div>
                            </div>

                            <div className="d-flex align-items-start">
                              <div className="bullet carbs"></div>
                              <div>
                                <span className="description small">
                                  {t('settings.dietType.macros.carbs')}
                                </span>
                                <br />
                                <span className="semi-bold">{preset.carbohydrate}%</span>
                              </div>
                            </div>
                          </div>

                          <Row>
                            <Col></Col>
                            <Col></Col>
                            <Col></Col>
                          </Row>

                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            className="w-100"
                            onClick={() => applyPreset(preset)}
                          >
                            {t('tracker.macroDistribution.preset.button')}
                          </Button>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        <div className="sticky-footer">
          <Container className="px-3">
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <Button
                  type="submit"
                  color="primary"
                  className="w-100"
                  disabled={total !== 100 || isSaving}
                  onClick={() => handleSaveMacro()}
                >
                  {t('tracker.macroDistribution.saveButton')}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </section>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="tracker.macroSettings.navbarTitile" />;

export default withLoading(withMobileAppMode(MacroSettingsContainer), Placeholder);
